import React, { memo, useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { projectsPortfolio } from '../../data/mock';

// Optimized image component with lazy loading
const LazyImage = memo(({ src, alt, className }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const imgRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: '100px', threshold: 0.01 }
    );

    if (imgRef.current) observer.observe(imgRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={imgRef} className="absolute inset-0">
      {/* Placeholder */}
      {!isLoaded && (
        <div className="absolute inset-0 bg-stone-200 animate-pulse" />
      )}
      {/* Image - only load when in view */}
      {isInView && (
        <img
          src={src}
          alt={alt}
          width={400}
          height={300}
          className={`${className} ${isLoaded ? 'opacity-100' : 'opacity-0'} transition-opacity duration-300`}
          onLoad={() => setIsLoaded(true)}
          loading="lazy"
          decoding="async"
        />
      )}
    </div>
  );
});

// Optimized project card
const ProjectCard = memo(({ project }) => (
  <Link
    to={project.href}
    className="group relative aspect-[4/3] rounded-sm overflow-hidden contain-layout"
  >
    {/* Background Image with lazy loading */}
    <LazyImage
      src={project.image}
      alt={project.name}
      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
    />
    
    {/* Overlay - using opacity instead of background changes */}
    <div className="absolute inset-0 bg-gradient-to-t from-stone-900/80 via-stone-900/30 to-transparent" />

    {/* Project Name Label */}
    <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-5">
      <p className="text-sm sm:text-base font-medium text-white text-center tracking-wide">
        {project.name}
      </p>
    </div>

    {/* Bottom Accent Line - transform-based animation */}
    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-blue-500 to-purple-500 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" />
  </Link>
));

export const ProjectsPortfolio = memo(() => {
  return (
    <section className="py-24 bg-white content-visibility-auto">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block text-xs font-semibold tracking-[0.2em] bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent uppercase mb-4">
            Portfolio
          </span>
          <h2 className="text-3xl lg:text-4xl font-light text-stone-900">
            Our Projects
          </h2>
        </div>

        {/* Projects Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-8">
          {projectsPortfolio.map((project) => (
            <ProjectCard key={project.id} project={project} />
          ))}
        </div>
      </div>
    </section>
  );
});
